import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAdminStats } from '../../services/admin';

// Professional SVG icons
const ICONS = {
  users: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  active: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 1 0-9.92 10"/>
      <path d="M12 20v-6"/>
      <path d="M6.53 9.53a5 5 0 0 1 7.07 7.07"/>
    </svg>
  ),
  payments: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
      <line x1="1" y1="10" x2="23" y2="10"/>
      <path d="M8 14h.01M12 14h.01M16 14h.01"/>
    </svg>
  ),
  pending: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v6l4 2"/>
    </svg>
  ),
  papers: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14,2 14,8 20,8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10,9 9,9 8,9"/>
    </svg>
  ),
  videos: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="23 7 16 12 23 17 23 7"/>
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
    </svg>
  ),
  exams: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  analytics: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 20V10"/>
      <path d="M18 4V4"/>
      <path d="M14 4h4"/>
      <path d="M14 20h4"/>
      <path d="M4 12h8"/>
      <path d="M4 16h8"/>
    </svg>
  )
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const adminStats = await getAdminStats();
        setStats(adminStats);
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  if (loading || !stats) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>Manage Zed Scholar platform</p>
      </div>

      {/* Stats Overview */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">{ICONS.users}</div>
          <div className="stat-content">
            <h3>{stats.totalUsers}</h3>
            <p>Total Users</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">{ICONS.active}</div>
          <div className="stat-content">
            <h3>{stats.activeUsers}</h3>
            <p>Active Subscriptions</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">{ICONS.payments}</div>
          <div className="stat-content">
            <h3>{stats.totalPayments}</h3>
            <p>Total Payments</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">{ICONS.pending}</div>
          <div className="stat-content">
            <h3>{stats.pendingPayments}</h3>
            <p>Pending Payments</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">{ICONS.papers}</div>
          <div className="stat-content">
            <h3>{stats.totalPapers}</h3>
            <p>Past Papers</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">{ICONS.videos}</div>
          <div className="stat-content">
            <h3>{stats.totalVideos}</h3>
            <p>Video Lessons</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">{ICONS.exams}</div>
          <div className="stat-content">
            <h3>{stats.totalExams}</h3>
            <p>Exams</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">{ICONS.analytics}</div>
          <div className="stat-content">
            <h3>{stats.totalUsers ? Math.round((stats.activeUsers / stats.totalUsers) * 100) : 0}%</h3>
            <p>Conversion Rate</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/admin/payments" className="action-card">
            <div className="action-icon">{ICONS.payments}</div>
            <h3>Manage Payments</h3>
            <p>Approve or reject payment submissions</p>
          </Link>

          <Link to="/admin/users" className="action-card">
            <div className="action-icon">{ICONS.users}</div>
            <h3>Manage Users</h3>
            <p>View and manage user accounts</p>
          </Link>

          <Link to="/admin/content/papers" className="action-card">
            <div className="action-icon">{ICONS.papers}</div>
            <h3>Upload Papers</h3>
            <p>Add past papers and mock exams</p>
          </Link>

          <Link to="/admin/content/videos" className="action-card">
            <div className="action-icon">{ICONS.videos}</div>
            <h3>Manage Videos</h3>
            <p>Add and edit video lessons</p>
          </Link>

          <Link to="/admin/content/exams" className="action-card">
            <div className="action-icon">{ICONS.exams}</div>
            <h3>Create Exams</h3>
            <p>Build practice exams</p>
          </Link>

          <Link to="/admin/content/subjects" className="action-card">
            <div className="action-icon">{ICONS.content}</div>
            <h3>Manage Subjects</h3>
            <p>Configure subjects and topics</p>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-activity">
        <div className="activity-section">
          <h2>Recent Users</h2>
          <div className="activity-list">
            {stats.recentUsers.map((user) => (
              <div key={user.id} className="activity-item">
                <div className="activity-info">
                  <h4>{user.firstName} {user.lastName}</h4>
                  <p>{user.email}</p>
                  <span className="activity-meta">
                    {user.levelId} • Joined {formatDate(user.createdAt)}
                  </span>
                </div>
                <div className="activity-status">
                  <span className={`status-badge ${user.subscription?.status}`}>
                    {user.subscription?.status || 'unpaid'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="activity-section">
          <h2>Recent Payments</h2>
          <div className="activity-list">
            {stats.recentPayments.map((payment) => (
              <div key={payment.id} className="activity-item">
                <div className="activity-info">
                  <h4>K{payment.amount}</h4>
                  <p>{payment.transactionId}</p>
                  <span className="activity-meta">
                    {payment.levelId} • {formatDate(payment.createdAt)}
                  </span>
                </div>
                <div className="activity-status">
                  <span className={`status-badge ${payment.status}`}>
                    {payment.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
